import React, { useState, useEffect } from 'react';
import { AppData, StoreType } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { Zap, Rocket, Flame, Bot, Briefcase, MessageSquare, Sparkles, ArrowUp, ArrowRight, Loader2 } from 'lucide-react';
import { getGrowthClassification } from '../components/GrowthScoreGauge';
import { appCatalog } from '../services/appCatalogService';

interface TrendingViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
}

export const TrendingView: React.FC<TrendingViewProps> = ({ onSelectApp, onCompare }) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<StoreType>('all');
  const [apps, setApps] = useState<AppData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const categories = [
    { id: 'all', label: 'All Trending', icon: Zap },
    { id: 'breakout', label: 'Breakout Apps', icon: Rocket },
    { id: 'fastest_growing', label: 'Fastest Growing', icon: Flame },
    { id: 'ai', label: 'Trending AI Apps', icon: Bot },
    { id: 'productivity', label: 'Trending Productivity', icon: Briefcase },
    { id: 'social', label: 'Trending Social', icon: MessageSquare }
  ];

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const categoryParam = activeCategory === 'all' || activeCategory === 'breakout' || activeCategory === 'fastest_growing'
      ? 'all'
      : activeCategory;

    appCatalog.searchApps({
      category: categoryParam,
      store: selectedStore,
      sort: 'trending',
      limit: 60
    }).then((res) => {
      if (isMounted) {
        let results = res.results || [];
        if (activeCategory === 'breakout') {
          results = results.filter((a) => a.growthClassification === 'Breakout' || a.growthScore >= 88);
        } else if (activeCategory === 'fastest_growing') {
          results = results.filter((a) => a.growthScore >= 80);
        }
        setApps(results);
        setIsLoading(false);
      }
    }).catch(() => {
      if (isMounted) setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [activeCategory, selectedStore]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold mb-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Live Market Movement Radar
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            Trending & Breakout Apps
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Detecting apps that are accelerating rapidly beyond their historical search & activity baselines.
          </p>
        </div>

        {/* Store Toggle */}
        <div className="flex items-center gap-2 self-start md:self-auto bg-slate-100 p-1 rounded-2xl text-xs font-semibold">
          {(['all', 'appstore', 'googleplay'] as const).map((st) => (
            <button
              key={st}
              onClick={() => setSelectedStore(st)}
              className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                selectedStore === st ? 'bg-white text-blue-600 shadow-sm font-bold' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {st === 'all' ? 'All Stores' : st === 'appstore' ? 'App Store' : 'Google Play'}
            </button>
          ))}
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        {categories.map((c) => {
          const IconComp = c.icon;
          const isActive = activeCategory === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold whitespace-nowrap transition-all border cursor-pointer ${
                isActive
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20'
                  : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
              }`}
            >
              <IconComp className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>{c.label}</span>
            </button>
          );
        })}
      </div>

      {/* Grid of Trending Cards */}
      {isLoading ? (
        <div className="py-20 flex flex-col items-center justify-center space-y-3">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-xs text-slate-500 font-medium">Computing trending velocity across catalog...</p>
        </div>
      ) : apps.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-200">
          <Zap className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <h3 className="font-bold text-slate-800">No Apps Found</h3>
          <p className="text-xs text-slate-500 mt-1">Try switching categories or stores.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {apps.map((app) => {
            const { label, bg } = getGrowthClassification(app.growthScore);
            const isBreakout = app.growthScore >= 88;
            const posGain = Math.max(1, app.previousRank - app.currentRank || 6);

            return (
              <div
                key={app.id}
                onClick={() => onSelectApp(app.id)}
                className={`bg-white rounded-3xl p-6 border transition-all duration-200 cursor-pointer group relative overflow-hidden flex flex-col justify-between ${
                  isBreakout
                    ? 'border-blue-200 shadow-md hover:shadow-xl hover:border-blue-400'
                    : 'border-slate-200/80 hover:border-slate-300 hover:shadow-lg'
                }`}
              >
                {isBreakout && (
                  <div className="absolute top-0 right-0 bg-gradient-to-l from-blue-600 to-sky-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-bl-xl shadow-xs inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" strokeWidth={2} />
                    <span>Breakout</span>
                  </div>
                )}

                <div>
                  <div className="flex items-start gap-3.5 mb-3">
                    <AppIcon
                      app={app}
                      size="md"
                      className="w-14 h-14 rounded-2xl shadow-sm group-hover:scale-105 transition-transform flex-shrink-0"
                    />
                    <div>
                      <h3 className="font-extrabold text-slate-900 text-base group-hover:text-blue-600 transition-colors line-clamp-1">
                        {app.name}
                      </h3>
                      <p className="text-xs text-slate-400">{app.developer}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StoreBadge store={app.primaryStore} />
                        <span className="text-[11px] text-slate-500 font-medium">{app.category}</span>
                      </div>
                    </div>
                  </div>

                  {/* Movement Box */}
                  <div className="bg-slate-50 rounded-2xl p-3 border border-slate-100 my-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 font-medium">Search Rank Shift:</span>
                      <span className="font-bold text-slate-900">
                        #{app.previousRank + posGain} → <strong className="text-blue-600">#{app.currentRank}</strong>
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                        <ArrowUp className="w-3.5 h-3.5" strokeWidth={2.5} />
                        <span>+{posGain} positions</span>
                      </span>
                      <span className="text-slate-400 text-[11px]">in observed searches</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                    {app.subtitle || app.description}
                  </p>
                </div>

                {/* Footer */}
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs mt-4">
                  <div>
                    <span className="text-slate-400 block text-[10px] uppercase font-bold">Growth Score</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${bg}`}>
                      {app.growthScore} • {label}
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="text-xs font-bold text-blue-600 group-hover:underline inline-flex items-center gap-1">
                      <span>Analyze App</span>
                      <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
